import { Alert, Button, CopyButton, ShieldCheckIcon, Spinner } from "@strust/ui";
import { css } from "@strust/ui/css";
import type { SyntheticEvent } from "react";
import { useIntl } from "react-intl";
import { resetFields, submitFields } from "./form-field.ts";
import { TotpCodeField, useTotpCodeField } from "./totp-code-field.tsx";
import { useCopyLabels } from "./use-copy-labels.ts";
import { TotpQrCode } from "./totp-qr-code.tsx";
import { twoFactorMessages } from "./two-factor-messages.ts";

const LAYOUT = css({ display: "flex", flexDirection: "column", gap: "4" });
const KEY = css({ display: "flex", flexDirection: "column", gap: "2" });
const SECRET = css({
  display: "flex",
  alignItems: "center",
  gap: "2",
  flexWrap: "wrap",
  fontFamily: "mono",
  fontSize: "md",
  letterSpacing: "0.08em",
  wordBreak: "break-all",
  color: "text",
});
const NOTE = css({ textStyle: "bodySm", color: "text.2", m: "0" });
const ACTIONS = css({ display: "flex", gap: "2", flexWrap: "wrap" });

export interface TotpSetupProps {
  /** The authenticator being added; `undefined` while it is being prepared. */
  readonly setup: { readonly otpauthUri: string; readonly secret: string } | undefined;
  readonly onConfirm: (code: string) => void;
  readonly pending: boolean;
  /** Text of the last refusal (already translated). */
  readonly error?: string | undefined;
  /** A secondary action (cancel, back to sign in). */
  readonly secondary?: { readonly label: string; readonly onClick: () => void } | undefined;
}

/**
 * Adding an authenticator app (design D5): the QR code drawn locally from the `otpauth://` URI, the
 * same secret as text, then the first code. Presentational: the secret comes in through props and
 * leaves the DOM as soon as the caller drops it.
 */
export function TotpSetup({ setup, onConfirm, pending, error, secondary }: TotpSetupProps) {
  const intl = useIntl();
  const copyLabels = useCopyLabels();
  const code = useTotpCodeField();

  if (setup === undefined)
    return (
      <p role="status" className={NOTE}>
        <Spinner /> {intl.formatMessage(twoFactorMessages.preparing)}
      </p>
    );

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!submitFields(code)) return;
    onConfirm(code.value);
    resetFields(code);
  };

  return (
    <form className={LAYOUT} onSubmit={submit} noValidate>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <p className={NOTE}>{intl.formatMessage(twoFactorMessages.scan)}</p>
      <TotpQrCode uri={setup.otpauthUri} label={intl.formatMessage(twoFactorMessages.qrLabel)} />
      <div className={KEY}>
        <p className={NOTE}>{intl.formatMessage(twoFactorMessages.secretLabel)}</p>
        <p className={SECRET} data-testid="totp-secret">
          <span>{setup.secret.replace(/(.{4})/g, "$1 ").trim()}</span>
          <CopyButton
            {...copyLabels}
            value={setup.secret}
            label={intl.formatMessage(twoFactorMessages.secretCopy)}
          />
        </p>
        <p className={NOTE}>{intl.formatMessage(twoFactorMessages.secretOnce)}</p>
      </div>
      <TotpCodeField field={code} />
      <div className={ACTIONS}>
        <Button type="submit" visual="primary" icon={<ShieldCheckIcon />} loading={pending}>
          {intl.formatMessage(twoFactorMessages.confirm)}
        </Button>
        {secondary ? (
          <Button visual="ghost" onClick={secondary.onClick} disabled={pending}>
            {secondary.label}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
